import { useAtom } from "jotai";
import { useCallback, useMemo, useRef, useEffect } from "react";
import { deviceMotionAtom } from "../atoms/deviceMotion";
import { useGravityUtils } from "./useGravityUtils";
import {
  bufferLimit,
  STATIONARY_MAG_DRIFT_THRESH_G,
  STATIONARY_DELTA_RMS_THRESH_G,
  STATIONARY_TILT_DRIFT_DEG,
  STATIONARY_ANGLE_JITTER_DEG,
  STATIONARY_SUM_VAR_SHORT_THRESH_G2,
  STATIONARY_SHORT_WINDOW_SAMPLES,
  STATIONARY_LONG_WINDOW_SAMPLES,
} from "../Utils/constants";
import throttle from "lodash.throttle";
import { ScanUploader } from "@/services/ScanUploader";
import { logInfo, logError } from "@/Utils/logger";

// Store last motion violation details for error logging
let lastMotionViolation: {
  timestamp: number;
  detected: string;
  details: Array<{ type: string; value: string; threshold: number; unit: string }>;
} | null = null;

export const getLastMotionViolation = () => lastMotionViolation;
export const clearLastMotionViolation = () => { lastMotionViolation = null; };

export const useDeviceMotion = () => {
  const [deviceMotionState, setDeviceMotionState] = useAtom(deviceMotionAtom);
  const {
    convertToG,
    computeStatistics,
    normalizeGravity,
    multiplyMatrixAndVector,
    angleBetweenDeg,
    rms,
  } = useGravityUtils();
  const scanUploader = useMemo(() => ScanUploader.getInstance(), []);
  const isDev = false; //process.env.NODE_ENV === "development";
  const activeListenerRef = useRef<((event: DeviceMotionEvent) => void) | null>(null);

  const handleMotionEvent = useCallback(
    throttle((event: DeviceMotionEvent) => {
      if (event.acceleration) {
        setDeviceMotionState((prev) => ({
          ...prev,
          supported: true,
          motionEvents: true,
          permitted: true,
          isDenied: false,
        }));
      } else {
        setDeviceMotionState((prev) => ({
          ...prev,
          supported: isDev ? true : false,
          motionEvents: isDev ? true : false,
        }));
      }

      const { x, y, z } = event.accelerationIncludingGravity || {
        x: 0,
        y: 0,
        z: 0,
      };
      const gravityVector: [number, number, number] = [x ?? 0, y ?? 0, z ?? 0];
      const gravityInCameraFrame = multiplyMatrixAndVector(gravityVector);

      const newSample = {
        x: gravityInCameraFrame[0],
        y: gravityInCameraFrame[1],
        z: gravityInCameraFrame[2],
      };

      const newSampleInG = convertToG(newSample);

      setDeviceMotionState((prev) => {
        const updatedBuffer = [...prev.gravityBuffer, newSampleInG].slice(-bufferLimit);
        const len = updatedBuffer.length;

        // Compute short and long window statistics
        const shortN = Math.min(STATIONARY_SHORT_WINDOW_SAMPLES, len);
        const longN = Math.min(STATIONARY_LONG_WINDOW_SAMPLES, len);
        const shortBuf = updatedBuffer.slice(len - shortN);
        const longBuf = updatedBuffer.slice(len - longN);

        const shortStats = computeStatistics(shortBuf);
        const longStats = computeStatistics(longBuf);

        // Movement/hand-held detection thresholds (in g units / degrees)
        const MAG_DRIFT_THRESH = STATIONARY_MAG_DRIFT_THRESH_G;
        const DELTA_RMS_THRESH = STATIONARY_DELTA_RMS_THRESH_G;
        const TILT_DRIFT_DEG = STATIONARY_TILT_DRIFT_DEG;
        const ANGLE_JITTER_DEG = STATIONARY_ANGLE_JITTER_DEG;
        const SUM_VAR_SHORT_THRESH = STATIONARY_SUM_VAR_SHORT_THRESH_G2;

        // Mean magnitude vs 1g
        const meanMagShort = Math.sqrt(
          shortStats.mean.x * shortStats.mean.x +
            shortStats.mean.y * shortStats.mean.y +
            shortStats.mean.z * shortStats.mean.z
        );
        const magDrift = Math.abs(meanMagShort - 1);

        // RMS of successive deltas over short buffer
        const deltas: number[] = [];
        for (let i = 1; i < shortBuf.length; i++) {
          const dx = shortBuf[i].x - shortBuf[i - 1].x;
          const dy = shortBuf[i].y - shortBuf[i - 1].y;
          const dz = shortBuf[i].z - shortBuf[i - 1].z;
          deltas.push(Math.sqrt(dx * dx + dy * dy + dz * dz));
        }
        const deltaRms = rms(deltas);

        // Orientation drift over long window (compare first half vs last half)
        const half = Math.max(1, Math.floor(longBuf.length / 2));
        const firstHalf = longBuf.slice(0, half);
        const lastHalf = longBuf.slice(longBuf.length - half);
        const firstMeanNorm = normalizeGravity(
          computeStatistics(firstHalf).mean
        );
        const lastMeanNorm = normalizeGravity(
          computeStatistics(lastHalf).mean
        );
        const driftDeg = angleBetweenDeg(firstMeanNorm, lastMeanNorm);

        // Orientation jitter over short window: angle vs short mean
        const shortMeanNorm = normalizeGravity(shortStats.mean);
        const angleErrs: number[] = shortBuf.map((s) =>
          angleBetweenDeg(shortMeanNorm, normalizeGravity(s))
        );
        const angleRms = rms(angleErrs);

        // Check movement conditions
        const movementDetected = magDrift > MAG_DRIFT_THRESH || deltaRms > DELTA_RMS_THRESH;
        const tiltDriftDetected = driftDeg > TILT_DRIFT_DEG;
        const sumVarShort = shortStats.variance.x + shortStats.variance.y + shortStats.variance.z;
        const handheldDetected = angleRms > ANGLE_JITTER_DEG || sumVarShort > SUM_VAR_SHORT_THRESH;

        // Final stationary decision: robust to footsteps/vibration
        // Consider stationary until buffer is fully filled; then apply checks.
        let isStationaryValue = true;
        if (len >= bufferLimit) {
          isStationaryValue = !(movementDetected || tiltDriftDetected || handheldDetected);

          // Store violation details for error logging (only logged when scan is cancelled)
          if (!isStationaryValue) {
            const details: Array<{ type: string; value: string; threshold: number; unit: string }> = [];

            if (movementDetected) {
              if (magDrift > MAG_DRIFT_THRESH) {
                details.push({
                  type: 'magDrift',
                  value: magDrift.toFixed(4),
                  threshold: MAG_DRIFT_THRESH,
                  unit: 'g'
                });
              }
              if (deltaRms > DELTA_RMS_THRESH) {
                details.push({
                  type: 'deltaRms',
                  value: deltaRms.toFixed(4),
                  threshold: DELTA_RMS_THRESH,
                  unit: 'g'
                });
              }
            }

            if (tiltDriftDetected) {
              details.push({
                type: 'driftDeg',
                value: driftDeg.toFixed(2),
                threshold: TILT_DRIFT_DEG,
                unit: '°'
              });
            }

            if (handheldDetected) {
              if (angleRms > ANGLE_JITTER_DEG) {
                details.push({
                  type: 'angleRms',
                  value: angleRms.toFixed(2),
                  threshold: ANGLE_JITTER_DEG,
                  unit: '°'
                });
              }
              if (sumVarShort > SUM_VAR_SHORT_THRESH) {
                details.push({
                  type: 'sumVarShort',
                  value: sumVarShort.toExponential(3),
                  threshold: SUM_VAR_SHORT_THRESH,
                  unit: 'g²'
                });
              }
            }

            // Store for later logging when error occurs
            lastMotionViolation = {
              timestamp: Date.now(),
              detected: [
                movementDetected && 'movement',
                tiltDriftDetected && 'tilt',
                handheldDetected && 'handheld'
              ].filter(Boolean).join(', ') || 'none',
              details
            };
          }
        }

        // Debug logs (development only)
        if (isDev) {
          if (len < bufferLimit) {
            logInfo("[isStationary] pre-fill", { len, bufferLimit, stationary: isStationaryValue });
          } else {
            logInfo(`[isStationary] Device is ${isStationaryValue ? 'stationary' : 'moving'}. Detected: ${
              [
                movementDetected && 'movement',
                tiltDriftDetected && 'tilt',
                handheldDetected && 'handheld'
              ].filter(Boolean).join(', ') || 'none'
            }`);
          }
        }

        let newGravityString = prev.gravityString;
        if (updatedBuffer.length === bufferLimit && isStationaryValue) {
          const normalizedGravity = normalizeGravity(longStats.mean);
          newGravityString = `${normalizedGravity.x} ${normalizedGravity.y} ${normalizedGravity.z}`;
          if (isDev) {
            logInfo("[isStationary] gravityString set", { gravity: normalizedGravity });
          }
        }
        // set the gravity string to the scan uploader
        // ensuring it will have the latest gravity string when the scan is uploaded
        scanUploader.setGravityString(newGravityString);

        return {
          ...prev,
          gravityBuffer: updatedBuffer,
          stationary: isStationaryValue,
          gravityString: newGravityString,
        };
      });
    }, 1000 / 5), // 5 hz
    []
  );

  const requestPermission = useCallback(async () => {
    let motionDetected = false;

    if (typeof (DeviceMotionEvent as any).requestPermission === "function") {
      logInfo("[DeviceMotion] detected - requesting permission");
      try {
        const permissionState = await (
          DeviceMotionEvent as any
        ).requestPermission();
        logInfo(`[DeviceMotion] permission result: ${permissionState}`);
        if (permissionState === "granted") {
          window.addEventListener("devicemotion", handleMotionEvent);
          activeListenerRef.current = handleMotionEvent;
          if (!deviceMotionState.permitted) {
            setDeviceMotionState((prev) => ({ ...prev, permitted: true, isChecked: true, isDenied: false }));
          }
        } else {
          setDeviceMotionState((prev) => ({
            ...prev,
            supported: true,
            permitted: false,
            motionEvents: false,
            isChecked: true,
            isDenied: true,
          }));
        }
      } catch (error) {
        logError("[DeviceMotion] permission request failed", { error });
        setDeviceMotionState((prev) => ({ ...prev, permitted: false, isChecked: true, isDenied: true }));
      }
    } else {
      const supportsMotionEvent =
        "DeviceMotionEvent" in window || "DeviceOrientationEvent" in window;

      logInfo(`[DeviceMotion] Motion event support: DeviceMotionEvent=${!!window.DeviceMotionEvent}, DeviceOrientationEvent=${!!window.DeviceOrientationEvent}`);

      if (!supportsMotionEvent) {
        logInfo("[DeviceMotion] No motion event support detected");
        setDeviceMotionState((prev) => ({
          ...prev,
          supported: false,
          permitted: false,
          isChecked: true,
        }));
        return;
      }

      logInfo("[DeviceMotion] Adding temporary motion listener for detection");
      const tempMotionHandler = (event: DeviceMotionEvent) => {
        if (
          event.acceleration?.x !== null ||
          event.accelerationIncludingGravity?.x !== null
        ) {
          motionDetected = true;
          logInfo("[DeviceMotion] Valid motion data detected, switching to main handler");
          window.removeEventListener("devicemotion", tempMotionHandler);
          window.addEventListener("devicemotion", handleMotionEvent);
          activeListenerRef.current = handleMotionEvent;
          if (!deviceMotionState.permitted) {
            setDeviceMotionState((prev) => ({
              ...prev,
              supported: true,
              permitted: true,
              isChecked: true,
              isDenied: false,
            }));
          }
        } else {
          logInfo("[DeviceMotion] Motion event received but no valid acceleration data");
        }
      };
      window.addEventListener("devicemotion", tempMotionHandler);

      setTimeout(() => {
        logInfo(`[DeviceMotion] Detection timeout reached. Motion detected: ${motionDetected}, isDev: ${isDev}`);

        if (!motionDetected) {
          logInfo("[DeviceMotion] No motion detected after timeout - marking as unsupported");
          setDeviceMotionState((prev) => ({
            ...prev,
            supported: false,
            permitted: false,
            isChecked: true,
          }));
        }
      }, 1000);

      setTimeout(() => {
        if (isDev) {
          logInfo("[DeviceMotion] Development mode - forcing motion support");
          setDeviceMotionState((prev) => ({
            ...prev,
            supported: true,
            permitted: true,
            motionEvents: true,
            isChecked: true,
            isDenied: false,
          }));
        }
      }, 500);
    }
  }, []);

  const cleanup = useCallback(() => {
    if (activeListenerRef.current) {
      window.removeEventListener("devicemotion", activeListenerRef.current);
      activeListenerRef.current = null;
      logInfo("[DeviceMotion] Event listener removed");
    }
  }, []);

  // Cleanup when component unmounts
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    deviceMotionState,
    requestPermission,
    cleanup,
  };
};
